import {Component, OnInit} from '@angular/core';
import {
    AbstractExtractConf,
    ConfiguratorEtlService,
    ConfiguratorTableService,
    EtlFilterDTO,
    FieldTable,
    FilterConf,
    SharedFileExtractConf,
    SqlLoadConf,
    SqlProcessingLoadConf
} from '@tj/tj-dmp-lib-api';
import {Router} from '@angular/router';
import {AbstractEtlUpdateComponent} from '../abstract-etl-update.component';
import {ConfirmationService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {TargetFieldDestination} from '../etl-update-design2/target-field-destination.interface';
import TypeImportEnum = AbstractExtractConf.TypeImportEnum;

// Extension temporaire de AbsctractProcessingLoadConf pour exposer updateContact (SqlProcessingLoadConf),
// en attendant la régénération des fichiers Swagger.
interface ProcessingLoadWithUpdateContact {
    updateContact?: boolean;
    typeLoad?: string;
}

@Component({
    selector: 'dmp-etl-update-filter',
    templateUrl: './etl-update-filter.component.html',
    styleUrls: ['./etl-update-filter.component.scss'],
    standalone: false
})
export class EtlUpdateFilterComponent extends AbstractEtlUpdateComponent implements OnInit {

    typeImportEnum = TypeImportEnum;
    etl: EtlFilterDTO;
    stateOptions: any[];
    valueTypeImport: AbstractExtractConf.TypeImportEnum = this.typeImportEnum.ADD;
    fieldSearch: string[];
    fieldChangeDetection: string[];
    /** Champ en cours de paramétrage pour un import de type update */
    fields: TargetFieldDestination[] = null;
    /** Mise à jour des données de contact (personal) existantes lors d'un réimport (mode Ajout et Mise à jour) */
    updateContact = false;
    fieldTypeByCode: { [code: string]: FieldTable.TypeEnum } = {};

    constructor(
        protected configuratorEtlService: ConfiguratorEtlService,
        protected tableResourceService: ConfiguratorTableService,
        protected router: Router,
        protected confirmationService: ConfirmationService,
        protected translate: TranslateService,
    ) {
        super(router, confirmationService, translate);
    }

    ngOnInit() {
        this.configuratorEtlService
            .getFilterUsingGET(this.tenant, this.id)
            .subscribe((filterDTO) => {
                this.etl = filterDTO;
                this.completChange.emit(filterDTO.complet);
                if (this.etl.extract == null) {
                    this.etl.extract = {
                        filtersExtract: [],
                        group: null
                    };
                }
                const extract = this.etl.extract as SharedFileExtractConf;
                this.valueTypeImport = extract.typeImport == null ? TypeImportEnum.ADD : extract.typeImport;
                this.fieldSearch = extract.updateImportFieldSearch;
                this.fieldChangeDetection = extract.updateImportFieldChangeDetection;
                const load = this.etl.load as ProcessingLoadWithUpdateContact;
                this.updateContact = load?.updateContact ?? false;
                this.loadFieldCategories();
            });

        // this.etl.extract.filtersExtract;
        this.stateOptions = [{label: this.translate.instant('etl.new.design.import-type-add'), value: this.typeImportEnum.ADD}, {
            label: this.translate.instant('etl.new.design.import-type-add-update'),
            value: this.typeImportEnum.UPDATE
        }];
    }

    addFilter() {
        this.etl.extract.filtersExtract.push({
            field: null,
            operator: FilterConf.OperatorEnum.ISEMPTY,
            value: null,
            values: [],
        });
    }

    removeFilter(index: number) {
        this.etl.extract.filtersExtract.splice(index, 1);
    }

    next() {
        // this.router.navigate(['/h/flux/import-v1/etl']);
        this.nextStep.emit({uuid: this.id});
    }

    save() {
        const etl: EtlFilterDTO = {
            id: this.id,
            extract: {
                typeObject: 'SharedFileExtractConf',
                filtersExtract: this.etl.extract.filtersExtract,
                group: this.etl.extract.group,
                typeImport: this.valueTypeImport,
                updateImportFieldSearch: this.valueTypeImport === TypeImportEnum.ADD ? null : this.fieldSearch,
                updateImportFieldChangeDetection: this.valueTypeImport === TypeImportEnum.ADD ? null : this.fieldChangeDetection,
            } as SharedFileExtractConf,
            load: {
                typeLoad: 'SqlProcessingLoadConf',
                updateContact: this.valueTypeImport === TypeImportEnum.UPDATE ? this.updateContact : false,
            } as ProcessingLoadWithUpdateContact as any,
        };

        //
        this.configuratorEtlService
            .updateFilterUsingPOST(this.tenant, etl)
            .subscribe((data) => {
                this.next();
            });
    }

    // Ces opérateurs nécessitent un pattern (format de la colonne source) et une valeur de référence.
    private static readonly DATE_OPERATORS = [
        'LESS_THAN_OR_EQUAL', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN_OR_EQUAL_DELTA', 'GREATER_THAN_OR_EQUAL_DELTA',
        'EQUAL_DATE'
    ];

    // Ces opérateurs nécessitent uniquement une valeur de référence (pas de pattern).
    private static readonly VALUE_ONLY_OPERATORS = [
        'EQUAL', 'NOT_EQUAL',
        'NUMBER_GREATER_THAN', 'NUMBER_GREATER_THAN_OR_EQUAL', 'NUMBER_LESS_THAN', 'NUMBER_LESS_THAN_OR_EQUAL'
    ];

    private static hasValue(value: string | number | Date | null | undefined): boolean {
        if (value == null) {
            return false;
        }
        // filter.value peut être une chaîne (API), un number (p-inputNumber) ou un Date (datepicker)
        // selon le champ ; seule la chaîne doit être vérifiée non vide une fois trimée.
        if (typeof value === 'number') {
            return !Number.isNaN(value);
        }
        return value instanceof Date || value.trim().length > 0;
    }

    isValid() {
        const listErreur = this.etl.extract.filtersExtract?.filter(f =>
            f.field == null ||
            f.operator == null ||
            (EtlUpdateFilterComponent.VALUE_ONLY_OPERATORS.includes(f.operator) && !EtlUpdateFilterComponent.hasValue(f.value)) ||
            (EtlUpdateFilterComponent.DATE_OPERATORS.includes(f.operator) &&
                (!EtlUpdateFilterComponent.hasValue(f.pattern) || !EtlUpdateFilterComponent.hasValue(f.value)))
        );
        return listErreur.length === 0;
    }

    setFieldsDetectionChange($event: string[]) {
        this.fieldChangeDetection = $event;
    }

    // Type SQL du champ destination (étape mapping) par colonne source, pour restreindre les opérateurs.
    // Le type technique (Date/DateTime/Int/Decimal_*/...) est utilisé plutôt que categoryField (métier,
    // ex. PRICE/BOOKING_REFERENCE) qui ne reflète pas de façon fiable si un champ est numérique ou daté.
    private loadFieldCategories() {
        const loads = ((this.etl.load as SqlProcessingLoadConf)?.loads ?? []) as SqlLoadConf[];
        const tableDests = [...new Set(loads.map(l => l.tableDest).filter((t): t is string => !!t))];
        if (tableDests.length === 0) {
            return;
        }
        this.tableResourceService.getTablesUsingGET(this.tenant, tableDests).subscribe((tables) => {
            // nouvelle référence (et non une mutation en place) pour que le binding [fieldTypeByCode]
            // déclenche bien ngOnChanges côté dmp-filter-etl
            const typeByCode = {...this.fieldTypeByCode};
            loads.forEach((load) => {
                if (!load.fieldSrc) {
                    return;
                }
                const metaField = tables
                    .find((t) => t.code === load.tableDest)
                    ?.fields?.find((f) => f.code === load.fieldDest);
                if (metaField?.type) {
                    typeByCode[load.fieldSrc] = metaField.type;
                }
            });
            this.fieldTypeByCode = typeByCode;
        });
    }
}
